import { Component, inject, Input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, delay, firstValueFrom, map, shareReplay, tap } from 'rxjs';

import { Content, Course, environment, Guide, SkillLevelId, SkillsGroupId, User, UserData } from 'src/index';
import { AuthenticationService, ConfigurationService, CoursesService, GuidesService, LoadingService, PopupService, RoutingService, SkillsService, UserService } from 'src/index/services.index';

@Component({
    selector: 'meta-user-skills',
    templateUrl: './user-skills.component.html',
    styleUrls: ['./user-skills.component.css']
})
export class UserSkillsComponent {
    popupService = inject(PopupService);

    @Input() skillsGroup: SkillsGroupId = this.currentRoute.snapshot.params['groupId'];

    config = {
        ...environment.userProfileModule,
        hideSuggestedCourses: environment.hideSuggestedCourses || environment.userProfileModule?.hideSuggestedCourses || false,
    };
    profilePath = this.configurationService.appSections.profile.routerLink;

    hideChatToggler = environment.hideChat || environment.hideChatToggler || this.config.hideChatToggler;

    userSkills: { [key: string]: SkillLevelId } = {};
    userSkillsForSuggestions$ = new BehaviorSubject<{ [key: string]: SkillLevelId } | undefined>(undefined);

    currentUser$ = this.authenticationService.currentUser$.pipe(
        tap(currentUser => {
            const userSkills = currentUser?.data?.skills ?? {};
            this.userSkills = {
                ...userSkills[this.skillsGroup] ?? {},
                ...this.userSkills,
            };
        }),
    );

    userSkillsGroupDetails$ = this.skillsService.getUserSkillsGroupDetails(this.skillsGroup).pipe(
        tap((details) => {
            if (details.skills?.length === 0) {
                this.router.navigateByUrl(this.profilePath)
            }
        }),
        shareReplay(1),
    );

    skillsGroupLabel$ = this.userSkillsGroupDetails$.pipe(
        map(details => details.label || this.skillsService.skillsGroupLabels[this.skillsGroup]),
    );

    userSkills$ = this.userSkillsGroupDetails$.pipe(
        map(details => details.skills),
        tap(skills => Object.keys(this.userSkills).forEach(skill => {
            if (!skills.find(s => s.id === skill)) {
                delete this.userSkills[skill];
            }
        })),
        delay(0),
        tap(() => this.userSkillsForSuggestions$.next({ ...this.userSkills })),
    );

    skillLevels$ = this.userSkillsGroupDetails$.pipe(
        map(details => details.levels),
    );

    submitted = false;

    errorMsg$ = new BehaviorSubject('');

    contentToPreview$ = new BehaviorSubject<Content | undefined>(undefined);
    $studyPathCourses = toSignal(this.coursesService.getStudyPathCourses().pipe(
        map(courses => courses || []),
        shareReplay(1),
    ));

    constructor(
        private authenticationService: AuthenticationService<User>,
        private configurationService: ConfigurationService,
        private coursesService: CoursesService,
        private currentRoute: ActivatedRoute,
        private guidesService: GuidesService,
        private loadingService: LoadingService,
        private router: Router,
        private routingService: RoutingService,
        private skillsService: SkillsService,
        private userService: UserService,
    ) {

    }

    async updateSkills() {
        this.loadingService.show();
        const currentUser = await firstValueFrom(this.currentUser$);
        if (currentUser) {
            currentUser.data = {
                ...currentUser.data || {} as UserData,
                skills: {
                    ...currentUser.data?.skills || {},
                    [this.skillsGroup]: this.userSkills,
                },
            };
            await firstValueFrom(this.userService.saveData(currentUser.data))
                .catch(() => this.popupService.error('An error occurred while saving the data'))
                .then(() => {
                    this.popupService.success('Data successfully updated!');
                });
        }
        this.loadingService.hide();
    }

    startCourse(course: Course, urlToOpen?: string | void) {
        if (course.associatedToUser) {
            this.goToCourse(course, urlToOpen);
            this.contentToPreview$.next(undefined);
        } else {
            this.loadingService.show();
            firstValueFrom(this.coursesService.startCourse(course))
                .then(() => {
                    this.contentToPreview$.next(undefined);
                    this.goToCourse(course, urlToOpen);
                })
                .catch((err) => this.popupService.error(err.message || this.configurationService.startCourseErrorMsg))
                .finally(() => this.loadingService.hide());
        }
    }

    goToCourse(course: Course, urlToOpen?: string | void) {
        if (urlToOpen) {
            return this.router.navigateByUrl(urlToOpen);
        }
        return this.coursesService.openCourse(course);
    }

    openGuide(guide: Guide) {
        firstValueFrom(this.guidesService.associateGuideToUser(guide.uuid))
            .finally(() => {
                this.contentToPreview$.next(undefined);
                if (guide.associatedToUser) {
                    // TODO: Aprire manuale all'ultima pagina visualizzata
                }
                this.routingService.goToGuide(guide.uuid, guide.startId);
            });
    }
}
